// frontend/src/pages/SellerProductsPage.jsx
import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Loader2, ChevronLeft, ChevronRight, Package as PackageIcon } from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';

// تابع تبدیل متن به slug
const slugify = (text) => {
  if (!text) return '';
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
    .replace(/[^\w\u0600-\u06FF\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export const SellerProductsPage = () => {
  const { user, token } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    price: '',
    stock: '',
    status: 'active',
    warranty_months: 12,
    brand: '',
    power_rating: '',
    show_price_on_call: false,
    contact_phone: '',
  });

  const pageSize = 20;

  useEffect(() => {
    fetchProducts(currentPage);
    fetchCategories();
  }, [currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(config.getApiUrl('/products/categories/'));
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = localStorage.getItem('access_token');
      const url = config.getApiUrl(`/products/products/my-products/?page=${page}&page_size=${pageSize}`);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: خطا در دریافت محصولات`);
      }

      const data = await response.json();
      setProducts(Array.isArray(data) ? data : (data.results || []));
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('خطا در دریافت محصولات. لطفاً مجدد تلاش کنید.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (slug, name) => {
    if (window.confirm(`آیا از حذف محصول "${name}" اطمینان دارید؟`)) {
      try {
        const accessToken = localStorage.getItem('access_token');
        const response = await fetch(config.getApiUrl(`/products/products/${slug}/`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          alert('محصول با موفقیت حذف شد');
          fetchProducts(currentPage);
        } else {
          alert('خطا در حذف محصول');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('خطا در ارتباط با سرور');
      }
    }
  };

  // ✅ اصلاح تابع handleInputChange برای slug
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'name') {
      const generatedSlug = slugify(value);
      setNewProduct(prev => ({ ...prev, name: value, slug: generatedSlug }));
    } else if (name === 'slug') {
      setNewProduct(prev => ({ ...prev, slug: value }));
    } else {
      setNewProduct(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newProduct.name || !newProduct.category) {
      alert('لطفاً نام و دسته‌بندی را وارد کنید');
      return;
    }

    // ✅ اعتبارسنجی قیمت: فقط اگر "تماس برای قیمت" فعال نباشد
    if (!newProduct.show_price_on_call) {
      if (!newProduct.price || parseInt(newProduct.price) <= 0) {
        alert('لطفاً قیمت را وارد کنید');
        return;
      }
    }

    if (!newProduct.stock || parseInt(newProduct.stock) < 0) {
      alert('لطفاً موجودی را وارد کنید');
      return;
    }

    let finalSlug = newProduct.slug;
    if (finalSlug) {
      finalSlug = slugify(finalSlug);
    }
    if (!finalSlug) {
      finalSlug = slugify(newProduct.name);
    }

    const accessToken = localStorage.getItem('access_token');
    setSubmitting(true);

    try {
      const productData = {
        name: newProduct.name,
        slug: finalSlug,
        category: parseInt(newProduct.category),
        description: newProduct.description,
        price: newProduct.show_price_on_call ? null : parseInt(newProduct.price),
        stock: parseInt(newProduct.stock),
        status: newProduct.status,
        warranty_months: parseInt(newProduct.warranty_months),
        brand: newProduct.brand || null,
        power_rating: newProduct.power_rating ? parseFloat(newProduct.power_rating) : null,
        specifications: {},
        show_price_on_call: newProduct.show_price_on_call,
        contact_phone: newProduct.contact_phone || null,

      };

      const response = await fetch(config.getApiUrl('/products/products/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert('محصول با موفقیت اضافه شد');
        setShowAddForm(false);
        setNewProduct({
          name: '',
          slug: '',
          category: '',
          description: '',
          price: '',
          stock: '',
          status: 'active',
          warranty_months: 12,
          brand: '',
          power_rating: '',
        });
        fetchProducts(currentPage);
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error adding product:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO
        title="مدیریت محصولات | پنل فروشنده"
        description="مدیریت محصولات، ویرایش و افزودن محصول جدید"
        noindex={true}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">مدیریت محصولات</h1>
              <p className="text-sm text-gray-500 mt-1">تعداد کل محصولات: {totalCount}</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5" />
              افزودن محصول جدید
            </button>
          </div>

          {error && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-6">
              {error}
              <button
                onClick={() => fetchProducts(currentPage)}
                className="mr-4 underline"
              >
                تلاش مجدد
              </button>
            </div>
          )}

          {/* فرم افزودن محصول */}
          {showAddForm && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-blue-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">افزودن محصول جدید</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نام محصول *</label>
                    <input
                      type="text"
                      name="name"
                      value={newProduct.name}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug
                      <span className="text-xs text-gray-500 mr-2">(اختیاری - در صورت خالی بودن از نام ساخته می‌شود)</span>
                    </label>
                    <input
                      type="text"
                      name="slug"
                      value={newProduct.slug}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                      placeholder="مثال: محصول-جدید-123"
                    />
                    <small className="text-xs text-gray-500 block mt-1">
                      آدرس نهایی: {config.SITE_URL}/products/{newProduct.slug || '...'}
                    </small>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی *</label>
                    <select
                      name="category"
                      value={newProduct.category}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    >
                      <option value="">انتخاب کنید</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">قیمت (تومان) *</label>
                    <input
                      type="number"
                      name="price"
                      value={newProduct.price}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">موجودی *</label>
                    <input
                      type="number"
                      name="stock"
                      value={newProduct.stock}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">گارانتی (ماه)</label>
                    <input
                      type="number"
                      name="warranty_months"
                      value={newProduct.warranty_months}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">برند</label>
                    <input
                      type="text"
                      name="brand"
                      value={newProduct.brand}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="مثال: JA Solar"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">توان (کیلووات)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="power_rating"
                      value={newProduct.power_rating}
                      onChange={handleInputChange}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="مثال: 5.5"
                    />
                  </div>
                  {/* ========== بخش تماس برای قیمت ========== */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">تنظیمات تماس برای قیمت</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          name="show_price_on_call"
                          checked={newProduct.show_price_on_call}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <label className="text-sm font-medium text-gray-700 cursor-pointer">
                            نمایش "تماس برای قیمت"
                          </label>
                          <p className="text-xs text-gray-500">در صورت فعال بودن، قیمت نمایش داده نمی‌شود و دکمه تماس نشان داده می‌شود</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">شماره تماس اختصاصی</label>
                        <input
                          type="text"
                          name="contact_phone"
                          value={newProduct.contact_phone}
                          onChange={handleInputChange}
                          className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="مثال: 09123456789"
                          disabled={!newProduct.show_price_on_call}
                        />
                        <p className="text-xs text-gray-400 mt-1">در صورت خالی بودن، شماره اصلی سایت نمایش داده می‌شود</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات *</label>
                  <textarea
                    name="description"
                    value={newProduct.description}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'در حال ذخیره...' : 'ذخیره محصول'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {products.length === 0 && !error ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <PackageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">شما هنوز محصولی اضافه نکرده‌اید</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-4 text-blue-600 hover:text-blue-700 underline"
              >
                اولین محصول خود را اضافه کنید
              </button>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تصویر</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نام محصول</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">برند</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">توان (kW)</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">قیمت</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">موجودی</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <img
                              src={product.main_image || 'https://via.placeholder.com/50x50?text=No+Image'}
                              className="w-12 h-12 object-cover rounded"
                              alt={product.name}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                              }}
                            />
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900 max-w-xs truncate">
                            {product.name}
                          </td>
                          <td className="px-6 py-4 text-gray-600">{product.brand || '—'}</td>
                          <td className="px-6 py-4 text-gray-600">{product.power_rating || '—'}</td>
                          <td className="px-6 py-4 text-gray-900">{product.price?.toLocaleString()} تومان</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${product.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {product.stock > 0 ? product.stock : 'ناموجود'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {product.status === 'active' ? 'فعال' : 'غیرفعال'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <Link
                                to={`/products/${product.slug}/edit`}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                title="ویرایش محصول"
                              >
                                <Edit className="w-5 h-5" />
                              </Link>
                              <button
                                onClick={() => deleteProduct(product.slug, product.name)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="حذف محصول"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6 pt-4">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border rounded-lg disabled:opacity-50"
                  >
                    قبلی
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg transition ${currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border rounded-lg disabled:opacity-50"
                  >
                    بعدی
                  </button>
                </div>
              )}

              <div className="text-center text-sm text-gray-500 mt-4">
                نمایش {products.length} از {totalCount} محصول
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};
